import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, DollarSign, Building2, Briefcase, TrendingUp, ShieldAlert, CheckCircle, XCircle } from "lucide-react";
import type { AnyValue } from "@/lib/rowTypes";

interface EventTemplate {
  id: string;
  event_key: string;
  event_name: string;
  event_category: string;
  event_difficulty: string;
  event_description: string;
  event_choices: AnyValue[];
  event_followup_text: string | null;
  event_trigger_type: string;
  event_frequency_weight: number;
  is_active: boolean;
}

interface LessonRewardInfo {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  reward_per_correct: number | null;
  completion_bonus: number | null;
  perfect_score_bonus: number | null;
  tier: string | null;
}

const CATEGORY_ICONS: Record<string, AnyValue> = {
  personal_finance: DollarSign,
  housing: Building2,
  career: Briefcase,
  investing: TrendingUp,
  entrepreneurship: Building2,
  scam_protection: ShieldAlert,
  behavior: AlertTriangle,
};

const CATEGORY_TARGETS: Record<string, number> = {
  personal_finance: 8,
  housing: 5,
  career: 5,
  investing: 5,
  entrepreneurship: 5,
  scam_protection: 5,
  behavior: 5,
};

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: "bg-emerald-500/10 text-emerald-600",
  intermediate: "bg-amber-500/10 text-amber-600",
  advanced: "bg-destructive/10 text-destructive",
};

export default function EventRewardAdminPanel() {
  const [events, setEvents] = useState<EventTemplate[]>([]);
  const [lessons, setLessons] = useState<LessonRewardInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const [{ data: evts }, { data: lsns }] = await Promise.all([
        supabase.from("life_event_templates").select("*").order("event_category"),
        supabase.from("lessons").select("lesson_id, lesson_name, course_name, reward_per_correct, completion_bonus, perfect_score_bonus, tier").order("course_name").order("lesson_order"),
      ]);
      setEvents((evts as AnyValue) || []);
      setLessons((lsns) || []);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  const categoryCounts = events.reduce((acc, e) => {
    acc[e.event_category] = (acc[e.event_category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const difficultyCounts = events.reduce((acc, e) => {
    acc[e.event_difficulty] = (acc[e.event_difficulty] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Reward tier summary
  const tiers = lessons.reduce((acc, l) => {
    const t = l.tier || "foundation";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const coursesWithCustomRewards = new Set(
    lessons.filter(l => l.reward_per_correct && l.reward_per_correct !== 5).map(l => l.course_name)
  ).size;

  return (
    <Tabs defaultValue="events" className="space-y-4">
      <TabsList className="grid grid-cols-3 w-full max-w-md">
        <TabsTrigger value="events">Events Library</TabsTrigger>
        <TabsTrigger value="rewards">Reward Rules</TabsTrigger>
        <TabsTrigger value="coverage">Coverage Map</TabsTrigger>
      </TabsList>

      {/* Events Library */}
      <TabsContent value="events" className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <SummaryCard label="Total Events" value={events.length} />
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <SummaryCard key={cat} label={cat.replace(/_/g, " ")} value={count} />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {Object.entries(difficultyCounts).map(([diff, count]) => (
            <div key={diff} className={`rounded-lg p-3 text-center ${DIFFICULTY_COLORS[diff] || "bg-muted"}`}>
              <p className="text-lg font-bold">{count}</p>
              <p className="text-[10px] uppercase tracking-wider capitalize">{diff}</p>
            </div>
          ))}
        </div>

        {/* Event list */}
        <div className="space-y-2">
          {events.map(evt => {
            const Icon = CATEGORY_ICONS[evt.event_category] || AlertTriangle;
            return (
              <div key={evt.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-sm text-foreground">{evt.event_name}</span>
                      <Badge variant="outline" className="text-[9px] capitalize">{evt.event_category.replace(/_/g, " ")}</Badge>
                      <Badge variant="outline" className={`text-[9px] ${DIFFICULTY_COLORS[evt.event_difficulty] || ""}`}>{evt.event_difficulty}</Badge>
                      {!evt.is_active && <Badge variant="destructive" className="text-[9px]">Inactive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{evt.event_description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] text-muted-foreground">{evt.event_choices.length} choices</span>
                      <span className="text-[10px] text-muted-foreground">Weight: {evt.event_frequency_weight}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </TabsContent>

      {/* Reward Rules */}
      <TabsContent value="rewards" className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <SummaryCard label="Total Lessons" value={lessons.length} />
          <SummaryCard label="Custom Rewards" value={coursesWithCustomRewards} />
          {Object.entries(tiers).map(([tier, count]) => (
            <SummaryCard key={tier} label={tier} value={count} />
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h3 className="font-heading font-semibold text-sm text-foreground">Default Reward Tiers</h3>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex justify-between"><span>Foundation</span><span>$5/correct · $25 completion · $15 perfect</span></div>
            <div className="flex justify-between"><span>Growth</span><span>$10/correct · $50 completion · $25 perfect</span></div>
            <div className="flex justify-between"><span>Strategic</span><span>$20/correct · $100 completion · $50 perfect</span></div>
          </div>
        </div>

        <div className="space-y-2">
          {lessons.slice(0, 20).map(l => (
            <div key={l.lesson_id} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{l.lesson_name}</p>
                <p className="text-[10px] text-muted-foreground">{l.course_name}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-muted-foreground">${l.reward_per_correct || 5}/q</span>
                <span className="text-[10px] text-muted-foreground">${l.completion_bonus || 25}</span>
                <Badge variant="outline" className="text-[9px] capitalize">{l.tier || "foundation"}</Badge>
              </div>
            </div>
          ))}
          {lessons.length > 20 && (
            <p className="text-xs text-center text-muted-foreground">+ {lessons.length - 20} more lessons</p>
          )}
        </div>
      </TabsContent>

      {/* Coverage Map */}
      <TabsContent value="coverage" className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <h3 className="font-heading font-semibold text-sm text-foreground">Event Category Coverage</h3>
          {Object.keys(CATEGORY_TARGETS).map(cat => {
            const count = categoryCounts[cat] || 0;
            const target = CATEGORY_TARGETS[cat];
            const complete = count >= target;
            return (
              <div key={cat} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {complete ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-destructive" />}
                  <span className="text-xs capitalize">{cat.replace(/_/g, " ")}</span>
                </div>
                <span className="text-xs text-muted-foreground">{count} / {target}+ events</span>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <h3 className="font-heading font-semibold text-sm text-foreground">Difficulty Distribution</h3>
          {["beginner", "intermediate", "advanced"].map(diff => {
            const count = difficultyCounts[diff] || 0;
            const pct = events.length ? Math.round((count / events.length) * 100) : 0;
            return (
              <div key={diff} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="capitalize">{diff}</span>
                  <span className="text-muted-foreground">{count} ({pct}%)</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <h3 className="font-heading font-semibold text-sm text-foreground">Difficulty Distribution</h3>
          {["beginner", "intermediate", "advanced"].map(diff => {
            const count = difficultyCounts[diff] || 0;
            const pct = events.length ? Math.round((count / events.length) * 100) : 0;
            return (
              <div key={diff} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="capitalize">{diff}</span>
                  <span className="text-muted-foreground">{count} ({pct}%)</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </TabsContent>
    </Tabs>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 text-center">
      <p className="text-lg font-bold text-foreground">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider capitalize">{label}</p>
    </div>
  );
}
